import { useDeferredValue, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Copy, RefreshCw, Search } from 'lucide-react';
import toast from 'react-hot-toast';
import TVChannelsLayout from '@/pages/content/TVChannelsLayout';
import Button from '@/components/ui/Button';
import DataTable from '@/components/ui/DataTable';
import AutocompleteSelect from '@/components/ui/AutocompleteSelect';
import StreamPlayerButton from '@/components/ui/StreamPlayerButton';
import { useIptvProviderChannels, useIptvProviders } from '@/hooks/useIptvProviders';
import { TableColumn } from '@/types';
import { IptvProviderChannel } from '@/services/iptvProviderService';

const TVChannelsProviderList = () => {
  const { providerId = '' } = useParams();
  const { data: providers = [], isLoading: isProvidersLoading } = useIptvProviders();
  const provider = providers.find((item) => item.id === providerId) ?? null;
  const {
    data: channels = [],
    isLoading,
    isError,
    error,
    refetch,
    isFetching,
  } = useIptvProviderChannels(provider);

  const [search, setSearch] = useState('');
  const [selectedGroup, setSelectedGroup] = useState('');

  const deferredSearch = useDeferredValue(search.trim().toLocaleLowerCase());
  const groups = Array.from(
    new Set(channels.map((channel) => channel.group).filter(Boolean))
  ).sort((a, b) => a.localeCompare(b));

  const filteredChannels = channels.filter((channel) => {
    const matchesSearch =
      !deferredSearch ||
      channel.title.toLocaleLowerCase().includes(deferredSearch) ||
      channel.group.toLocaleLowerCase().includes(deferredSearch);

    const matchesGroup = !selectedGroup || channel.group === selectedGroup;

    return matchesSearch && matchesGroup;
  });

  const handleCopyUrl = async (channel: IptvProviderChannel) => {
    if (!channel.url) {
      toast.error('Este canal no tiene URL disponible');
      return;
    }

    try {
      await navigator.clipboard.writeText(channel.url);
      toast.success('URL copiada');
    } catch {
      toast.error('No se pudo copiar la URL');
    }
  };

  const columns: TableColumn<IptvProviderChannel>[] = [
    {
      key: 'logo',
      header: 'Logo',
      accessor: (channel) => (
        <div className="flex h-10 w-16 items-center justify-center overflow-hidden rounded border border-gray-200 bg-gray-50">
          {channel.logo ? (
            <img
              src={channel.logo}
              alt={channel.title}
              className="max-h-9 max-w-14 object-contain"
              onError={(event) => {
                event.currentTarget.style.display = 'none';
              }}
            />
          ) : (
            <span className="text-xs text-gray-400">N/A</span>
          )}
        </div>
      ),
      className: 'w-24',
    },
    {
      key: 'title',
      header: 'Titulo',
      accessor: (channel) => (
        <span className="block max-w-[320px] truncate font-medium text-gray-900" title={channel.title}>
          {channel.title}
        </span>
      ),
      className: 'w-[320px]',
    },
    {
      key: 'group',
      header: 'Grupo',
      accessor: (channel) => <span className="text-gray-700">{channel.group}</span>,
    },
    {
      key: 'play',
      header: '',
      accessor: (channel) => (
        <StreamPlayerButton
          title={channel.title}
          streamUrl={channel.url}
          playLabel="Reproducir"
          noPlayableStreamMessage="Este canal no tiene URL disponible"
          rtspInlineNotSupportedMessage="Los streams RTSP no se pueden reproducir en el navegador."
        />
      ),
      align: 'center',
      className: 'w-14',
    },
    {
      key: 'copyUrl',
      header: '',
      accessor: (channel) => (
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-8 px-2"
          onClick={() => handleCopyUrl(channel)}
          title="Copiar URL"
          disabled={!channel.url}
        >
          <Copy className="h-4 w-4" />
        </Button>
      ),
      align: 'center',
      className: 'w-14',
    },
  ];

  return (
    <TVChannelsLayout>
      <div className="space-y-4">
        {isProvidersLoading && (
          <div className="rounded-md bg-gray-50 p-4 text-sm text-gray-600">
            Cargando proveedor...
          </div>
        )}

        {!isProvidersLoading && !provider && (
          <div className="rounded-md bg-red-50 p-4 text-sm text-red-700">
            No se encontro el proveedor solicitado.
          </div>
        )}

        {isError && (
          <div className="rounded-md bg-red-50 p-4 text-sm text-red-700">
            {error instanceof Error ? error.message : 'No se pudo cargar la lista del proveedor'}
          </div>
        )}

        {!isLoading && provider && (
          <DataTable
            data={filteredChannels}
            columns={columns}
            paginate={false}
            tableStyle="compact"
            virtualizeRows={filteredChannels.length > 200}
            estimatedRowHeight={44}
            headerRenderer={({ isCompact }) => (
              <div className={`${isCompact ? 'px-4 py-3' : 'px-6 py-4'} space-y-4`}>
                <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                  <div className="flex items-center gap-3">
                    <Link to="/content/tv-channels/providers" className="inline-flex items-center gap-2 text-sm font-medium text-indigo-600 hover:text-indigo-700">
                        <ArrowLeft className="h-4 w-4" />
                    </Link>
                    <h2 className="text-lg font-semibold text-gray-900">
                      {provider.name}
                    </h2>
                  </div>
                  <div className="grid min-w-0 grid-cols-1 gap-4 md:w-full md:grid-cols-[minmax(0,1fr)_260px_auto] lg:max-w-4xl">
                    <div className="relative">
                      <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input
                        type="text"
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                        placeholder="Buscar por canal o grupo"
                        disabled={!provider}
                        className="h-9 w-full rounded-md border border-gray-300 pl-10 pr-4 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>
                    <AutocompleteSelect
                      value={selectedGroup}
                      onChange={(value) => setSelectedGroup(value)}
                      options={groups.map((group) => ({ value: group, label: group }))}
                      placeholder="Todos los grupos"
                      disabled={!provider || groups.length === 0}
                      size={isCompact ? 'sm' : 'md'}
                      className="h-9"
                      allowClear
                      emptyMessage="No hay grupos disponibles"
                    />
                    <Button size="sm" variant="outline" onClick={() => refetch()} disabled={isFetching || !provider} className="h-9">
                      <RefreshCw className={`mr-1 h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
                      Refrescar
                    </Button>
                  </div>
                </div>
              </div>
            )}
          />
        )}

        {isLoading && (
          <div className="rounded-md border border-gray-200 bg-white px-4 py-8 text-center text-sm text-gray-500">
            Cargando lista de canales...
          </div>
        )}
      </div>
    </TVChannelsLayout>
  );
};

export default TVChannelsProviderList;
